import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars, faBell, faChevronRight, faLocationDot, faUsers,
  faChild, faPenToSquare, faPlus, faHome, faCalendarDays,
  faUser, faCalendarCheck, faClock, faChartSimple, faTrash,
  faUserGroup, faUserTie, faGear, faArrowLeft, faSpinner,
  faExclamationTriangle, faEye, faEdit, faMapMarkerAlt, faStar,
  faSignOutAlt, faCreditCard, faWarning
} from '@fortawesome/free-solid-svg-icons';
import { 
  dashboardService, 
  DashboardUser, 
  UserDashboardStats, 
  CoordinatorDashboardStats, 
  AdminDashboardStats,
  PendingPayment
} from '../services/dashboardService';
import { CoordinatorUpgradeService } from '../services/coordinatorUpgradeService';
import { notificationService } from '../services/notificationService';
import { PaymentService } from '../services/paymentService';
import { supabase } from '../lib/supabase';
import { storePayFastCheckout } from '../utils/payfastCheckout';
import CoordinatorUpgradeModal from '../components/CoordinatorUpgradeModal';
import NotificationBell from '../components/NotificationBell';
import { isLoggedIn, logoutUser, getCurrentUser } from '../utils/auth';


type DashboardRole = 'user' | 'coordinator' | 'admin';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState<DashboardRole>('user');
  const [currentUser, setCurrentUser] = useState<DashboardUser | null>(null);
  const [userStats, setUserStats] = useState<UserDashboardStats | null>(null);
  const [coordinatorStats, setCoordinatorStats] = useState<CoordinatorDashboardStats | null>(null);
  const [adminStats, setAdminStats] = useState<AdminDashboardStats | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Coordinator upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isCoordinator, setIsCoordinator] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [canCreateEvent, setCanCreateEvent] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  // Outstanding payments state
  const [hasOutstandingPayments, setHasOutstandingPayments] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    if (!isLoggedIn()) {
      navigate('/login');
      return;
    }
    
    loadDashboardData();
    
    // Load initial notification count
    setNotificationCount(notificationService.getUnreadCount());
    
    // Subscribe to notification changes
    const unsubscribe = notificationService.subscribe(() => {
      setNotificationCount(notificationService.getUnreadCount());
    });

    return unsubscribe;
  }, [navigate]);

  // Check for outstanding payments when currentUser changes
  useEffect(() => {
    async function checkOutstanding() {
      if (currentUser?.id) {
        const hasOutstanding = await dashboardService.hasOutstandingPayments(currentUser.id);
        setHasOutstandingPayments(hasOutstanding);
      }
    }
    checkOutstanding();
  }, [currentUser]);

  useEffect(() => {
    // Load role-specific data when role changes
    loadRoleSpecificData();
  }, [activeRole, currentUser]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load current user
      const user = await dashboardService.getCurrentDashboardUser();
      setCurrentUser(user);
      setActiveRole(user.role);

      // Load notifications
      const userNotifications = await dashboardService.getUserNotifications(user.id);
      setNotifications(userNotifications);

      // Load coordinator upgrade status if user has email
      if (user.email) {
        await loadCoordinatorUpgradeData(user.email);
      }

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadCoordinatorUpgradeData = async (userEmail: string) => {
    try {
      const [coordinatorStatus, credits, eventCreationAbility] = await Promise.all([
        CoordinatorUpgradeService.isUserCoordinator(userEmail),
        CoordinatorUpgradeService.getAvailableCredits(userEmail),
        CoordinatorUpgradeService.canCreateEvent(userEmail)
      ]);

      setIsCoordinator(coordinatorStatus);
      setAvailableCredits(credits);
      setCanCreateEvent(eventCreationAbility.canCreate);
    } catch (error) {
      console.error('Error loading coordinator upgrade data:', error);
    }
  };

  const loadRoleSpecificData = async () => {
    if (!currentUser) return;

    try {
      switch (activeRole) {
        case 'user':
          const userDashboardStats = await dashboardService.getUserDashboardStats(currentUser.id);
          setUserStats(userDashboardStats);
          break;
        case 'coordinator':
          const coordinatorDashboardStats = await dashboardService.getCoordinatorDashboardStats();
          setCoordinatorStats(coordinatorDashboardStats);
          break;
        case 'admin':
          const adminDashboardStats = await dashboardService.getAdminDashboardStats();
          setAdminStats(adminDashboardStats);
          break;
      }
    } catch (err) {
      console.error(`Error loading ${activeRole} dashboard data:`, err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getUnreadNotificationCount = () => {
    return notifications.filter(n => !n.read).length;
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  const handleCompletePayment = async (payment: any) => {
    try {
      // Get event and organizer information
      const { data: event } = await supabase
        .from('events')
        .select('organizer_id, title')
        .eq('id', payment.eventId)
        .single();

      if (!event || !event.organizer_id) {
        alert('Unable to process payment. Event information not found.');
        return;
      }

      // Create payment request
      const paymentRequest = {
        registrationId: payment.registrationId,
        eventId: payment.eventId,
        organizerId: event.organizer_id,
        amount: payment.amount,
        currency: 'ZAR',
        userEmail: currentUser?.email || '',
        userName: currentUser?.name || '',
        userId: currentUser?.id || ''
      };

      // Process payment through PayFast
      const paymentResponse = await PaymentService.processPayment(paymentRequest);

      if (paymentResponse.payfastCheckout) {
        storePayFastCheckout(paymentResponse.payfastCheckout);
        navigate('/payfast-checkout');
        return;
      }

      if (paymentResponse.success) {
        console.log('Payment initiated successfully');
      } else {
        alert(`Payment failed: ${paymentResponse.error}`);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Failed to process payment. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin mb-4" />
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-4">Error Loading Dashboard</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button 
          onClick={loadDashboardData}
          className="bg-primary text-white px-6 py-2 rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 font-sans text-dark">
      {/* Outstanding Payments Banner */}
      {hasOutstandingPayments && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
          <p>
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            You have outstanding payments! Please <Link to="#pending-payments" className="underline">complete your payment</Link> to avoid losing your registration.
          </p>
        </div>
      )}
      {/* Header */}
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
          </Link>
          <h1 className="app-header-title">Dashboard</h1>
          <div className="flex items-center space-x-3">
            <NotificationBell />
            <button 
              onClick={handleLogout}
              className="text-gray-300 hover:text-red-400 transition-colors"
              title="Logout"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="text-lg" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="content-container pb-20">
        {/* User Welcome Section */}
        <section className="bg-white p-4 mb-3">
          <div className="flex items-center">
            <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-200 border-2 border-primary flex items-center justify-center">
              {currentUser?.profile_image_url ? (
                <img 
                  src={currentUser.profile_image_url} 
                  alt="Profile" 
                  className="w-full h-full object-cover" 
                />
              ) : (
                <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xl" />
              )}
            </div>
            <div className="ml-3">
              <h2 className="text-lg font-bold">
                {currentUser?.id === 'guest-user' ? 'Welcome, Guest' : `Welcome, ${currentUser?.name?.split(' ')[0] || 'User'}`}
              </h2>
              <p className="text-gray-600 text-xs">
                {currentUser?.id === 'guest-user' ? (
                  <>
                    <Link to="/register" className="text-primary underline">Create an account</Link> to track your registrations
                  </>
                ) : (
                  <>Last login: {currentUser?.last_login ? new Date(currentUser.last_login).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Today'}, {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</>
                )}
              </p>
            </div>
          </div>
        </section>

        {/* Role-based Content (User View) */}
        <section className={activeRole !== 'user' ? 'hidden' : 'mb-3'}>
          {/* Coordinator Upgrade Section */}
          {currentUser?.email && (
            <div className="bg-white mb-3">
              <div className="px-4 py-3 border-b flex justify-between items-center">
                <h3 className="font-semibold text-gray-800">Event Coordinator</h3>
                {isCoordinator && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                    <FontAwesomeIcon icon={faStar} className="mr-1" />
                    Active
                  </span>
                )}
              </div>
              <div className="p-4">
                {isCoordinator ? (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-green-800">Coordinator Status</span>
                        <FontAwesomeIcon icon={faStar} className="text-green-600" />
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Available Credits:</span>
                          <span className="ml-2 font-semibold text-green-600">{availableCredits}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Can Create Events:</span>
                          <span className="ml-2 font-semibold">
                            {canCreateEvent ? (
                              <span className="text-green-600">Yes</span>
                            ) : (
                              <span className="text-red-600">No Credits</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowUpgradeModal(true)}
                        className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors"
                      >
                        {availableCredits > 0 ? 'Buy More Credits' : 'Buy Event Credit'}
                      </button>
                      <Link
                        to="/coordinator/events"
                        className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm text-center hover:bg-gray-200 transition-colors"
                      >
                        Manage Events
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <h4 className="font-medium text-orange-800 mb-2">Become an Event Coordinator</h4>
                      <p className="text-sm text-orange-700 mb-3">
                        Create and manage mountain biking events for the community. Pay R300 to become a coordinator and get 1 event creation credit.
                      </p>
                      <ul className="text-xs text-orange-600 space-y-1">
                        <li>• Create professional event listings</li>
                        <li>• Manage participant registrations</li>
                        <li>• Track event statistics and payments</li>
                        <li>• Build your reputation as an organizer</li>
                      </ul>
                    </div>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <FontAwesomeIcon icon={faStar} />
                      <span>Become Coordinator - R300</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* User's Upcoming Events */}
          <div className="bg-white mb-3">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Your Upcoming Events</h3>
              <Link to="/events" className="text-primary text-sm font-medium flex items-center">
                View All <FontAwesomeIcon icon={faChevronRight} className="ml-1 text-xs" />
              </Link>
            </div>
            <div className="p-4">
              {userStats?.upcomingEvents && userStats.upcomingEvents.length > 0 ? (
                userStats.upcomingEvents.slice(0, 2).map((event) => (
                  <div key={event.id} className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{event.title}</h4>
                        <p className="text-gray-600 text-xs mt-1">
                          {formatDate(event.date)} • {formatTime(event.date)}
                        </p>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          event.paid
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {event.paid ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                    <div className="mt-3 flex text-sm text-gray-600">
                      <div className="mr-4 flex items-center">
                        <FontAwesomeIcon icon={faLocationDot} className="mr-1 text-gray-400" />
                        <span className="text-xs">{event.location}</span>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-between">
                      <div className="flex items-center text-xs text-gray-500">
                        <FontAwesomeIcon icon={faUsers} className="mr-1" />
                        <span>
                          {event.registrationCount} participant{event.registrationCount === 1 ? '' : 's'} registered
                        </span>
                      </div>
                      <Link 
                        to={`/events/${event.id}`}
                        className="text-xs bg-primary text-white px-3 py-1 rounded-full"
                      >
                        Details
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <FontAwesomeIcon icon={faCalendarDays} className="text-4xl text-gray-300 mb-4" />
                  {currentUser?.id === 'guest-user' ? (
                    <>
                      <p className="text-gray-500 mb-2">No registered events</p>
                      <p className="text-gray-400 text-sm mb-4">
                        <Link to="/register" className="text-primary underline">Create an account</Link> and register for events to see them here
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-500">No upcoming events</p>
                  )}
                  <Link to="/events" className="text-primary text-sm mt-2 inline-block">
                    Browse available events
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* User's Registered Minors */}
          <div className="bg-white">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Registered Minors</h3>
              <Link to="/add-minor" className="text-primary text-sm font-medium flex items-center">
                <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add
              </Link>
            </div>
            <div className="p-4">
              <div className="flex space-x-3 overflow-x-auto">
                {userStats?.userMinors && userStats.userMinors.length > 0 ? (
                  userStats.userMinors.map((minor) => (
                    <div key={minor.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200 min-w-[200px]">
                      <div className="flex items-center mb-2">
                        <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-pink-500 mr-2">
                          <FontAwesomeIcon icon={faChild} />
                        </div>
                        <div>
                          <h4 className="font-medium">{minor.name}</h4>
                          <span className="text-xs text-gray-500">
                            {new Date().getFullYear() - new Date(minor.date_of_birth).getFullYear()} years • {minor.gender}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                          {minor.rider_category}
                        </span>
                        <Link to="/profile" className="text-primary text-sm">
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 w-full">
                    <FontAwesomeIcon icon={faChild} className="text-4xl text-gray-300 mb-4" />
                    {currentUser?.id === 'guest-user' ? (
                      <>
                        <p className="text-gray-500 mb-2">No minors registered</p>
                        <p className="text-gray-400 text-sm mb-4">
                          <Link to="/register" className="text-primary underline">Create an account</Link> to register minors for family events
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-500">No minors registered</p>
                    )}
                    <Link to="/add-minor" className="text-primary text-sm mt-2 inline-block">
                      Add a minor
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pending Payments */}
          <div className="bg-white mb-3">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Pending Payments</h3>
              {userStats?.pendingPayments && userStats.pendingPayments.length > 0 && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                  {userStats.pendingPayments.length} pending
                </span>
              )}
            </div>
            <div className="p-4">
              {userStats?.pendingPayments && userStats.pendingPayments.length > 0 ? (
                userStats.pendingPayments.map((payment) => (
                  <div key={payment.id} className="bg-yellow-50 rounded-lg p-3 mb-3 border border-yellow-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <FontAwesomeIcon icon={faWarning} className="text-yellow-600 mr-2" />
                          <h4 className="font-medium text-yellow-800">{payment.eventTitle}</h4>
                        </div>
                        <p className="text-yellow-700 text-xs mb-2">
                          Event Date: {formatDate(payment.date)} • {formatTime(payment.date)}
                        </p>
                        <p className="text-yellow-700 text-sm font-medium">
                          Amount Due: R{payment.amount?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-yellow-600 text-xs mt-1">
                          Reference: {payment.paymentReference}
                        </p>
                      </div>
                      <div className="flex flex-col space-y-2">
                        <button 
                          onClick={() => handleCompletePayment(payment)}
                          className="bg-yellow-600 text-white px-3 py-1 rounded text-xs hover:bg-yellow-700 transition-colors"
                        >
                          Pay Now
                        </button>
                        <Link 
                          to={`/events/${payment.eventId}`}
                          className="text-yellow-700 text-xs text-center hover:text-yellow-800"
                        >
                          Event Details
                        </Link>
                      </div>
                    </div>
                    <div className="mt-3 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
                      <FontAwesomeIcon icon={faClock} className="mr-1" />
                      Complete payment before the event date to secure your spot
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <FontAwesomeIcon icon={faCreditCard} className="text-4xl text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-2">No pending payments</p>
                  <p className="text-gray-400 text-sm mb-4">
                    All your event registrations are up to date
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Coordinator View */}
        <section className={activeRole !== 'coordinator' ? 'hidden' : 'mb-3'}>
          {/* Coordinator Credits Section */}
          <div className="bg-white mb-3">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Event Creation Credits</h3>
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="text-orange-600 text-sm font-medium flex items-center hover:text-orange-700"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" /> Buy Credits
              </button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-green-50 rounded-lg p-3 border border-green-100 text-center">
                  <div className="text-green-500 mb-1">
                    <FontAwesomeIcon icon={faStar} className="text-xl" />
                  </div>
                  <p className="text-xs text-gray-600">Available</p>
                  <h4 className="text-xl font-bold text-green-600">{availableCredits}</h4>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 text-center">
                  <div className="text-blue-500 mb-1">
                    <FontAwesomeIcon icon={faCalendarCheck} className="text-xl" />
                  </div>
                  <p className="text-xs text-gray-600">Can Create</p>
                  <h4 className="text-xl font-bold text-blue-600">
                    {canCreateEvent ? 'Yes' : 'No'}
                  </h4>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-100 text-center">
                  <div className="text-orange-500 mb-1">
                    <FontAwesomeIcon icon={faChartSimple} className="text-xl" />
                  </div>
                  <p className="text-xs text-gray-600">Cost per Credit</p>
                  <h4 className="text-lg font-bold text-orange-600">R300</h4>
                </div>
              </div>
              
              {!canCreateEvent && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500" />
                    <span className="text-sm font-medium text-red-800">No Credits Available</span>
                  </div>
                  <p className="text-sm text-red-700 mt-1">
                    You need to purchase event creation credits to create new events. Each credit costs R300 and allows you to create one event.
                  </p>
                </div>
              )}
              
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold mb-1">How Credits Work:</p>
                <p>• Each R300 payment gives you 1 event creation credit</p>
                <p>• Use 1 credit to create and publish 1 event</p>
                <p>• Credits never expire and can be used anytime</p>
                <p>• Purchase additional credits as needed</p>
              </div>
            </div>
          </div>

          {/* Coordinator Welcome Message */}
          {coordinatorStats && coordinatorStats.activeEvents === 0 && coordinatorStats.totalParticipants === 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faUserTie} className="text-orange-500 text-xl" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-orange-800">Welcome, Event Coordinator!</h3>
                  <p className="text-sm text-orange-700 mt-1">
                    As a coordinator, you can create and manage mountain biking events for the community. 
                    {canCreateEvent 
                      ? 'You have credits available - start by creating your first event!'
                      : 'Purchase event creation credits to start creating events.'
                    }
                  </p>
                  <div className="mt-3">
                    {canCreateEvent ? (
                      <Link 
                        to="/coordinator/events" 
                        className="text-sm bg-orange-600 text-white px-3 py-1.5 rounded-md hover:bg-orange-700 transition-colors"
                      >
                        Create Your First Event
                      </Link>
                    ) : (
                      <button
                        onClick={() => setShowUpgradeModal(true)}
                        className="text-sm bg-orange-600 text-white px-3 py-1.5 rounded-md hover:bg-orange-700 transition-colors"
                      >
                        Buy Event Credits
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white mb-3">
            <div className="px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-800">Your Event Statistics</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                <div className="text-orange-500 mb-1">
                  <FontAwesomeIcon icon={faCalendarCheck} className="text-xl" />
                </div>
                <p className="text-xs text-gray-600">Active Events</p>
                <h4 className="text-xl font-bold">{coordinatorStats?.activeEvents || 0}</h4>
              </div>
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <div className="text-green-500 mb-1">
                  <FontAwesomeIcon icon={faUsers} className="text-xl" />
                </div>
                <p className="text-xs text-gray-600">Total Participants</p>
                <h4 className="text-xl font-bold">{coordinatorStats?.totalParticipants || 0}</h4>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <div className="text-purple-500 mb-1">
                  <FontAwesomeIcon icon={faClock} className="text-xl" />
                </div>
                <p className="text-xs text-gray-600">Upcoming Events</p>
                <h4 className="text-xl font-bold">{coordinatorStats?.upcomingEvents || 0}</h4>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <div className="text-amber-500 mb-1">
                  <FontAwesomeIcon icon={faChartSimple} className="text-xl" />
                </div>
                <p className="text-xs text-gray-600">Registration Rate</p>
                <h4 className="text-xl font-bold">{coordinatorStats?.registrationRate || 0}%</h4>
              </div>
            </div>
          </div>

          <div className="bg-white">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Manage Your Events</h3>
              {canCreateEvent ? (
                <Link to="/coordinator/events" className="text-primary text-sm font-medium flex items-center">
                  <FontAwesomeIcon icon={faPlus} className="mr-1" /> Create
                </Link>
              ) : (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="text-orange-600 text-sm font-medium flex items-center hover:text-orange-700"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" /> Buy Credits
                </button>
              )}
            </div>
            <div className="p-4">
              {coordinatorStats?.managedEvents && coordinatorStats.managedEvents.length > 0 ? (
                coordinatorStats.managedEvents.map((event) => (
                  <div key={event.id} className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{event.title}</h4>
                        <p className="text-gray-600 text-xs mt-1">
                          {formatDate(event.date)} • {formatTime(event.date)}
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        <Link to={`/events/${event.id}`} className="text-primary p-1">
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <button className="text-primary p-1">
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </button>
                        <button className="text-red-500 p-1">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-between">
                      <div className="flex items-center text-xs text-gray-500">
                        <FontAwesomeIcon icon={faUsers} className="mr-1" />
                        <span>{event.current_participants || 0}/{event.max_participants} registered</span>
                      </div>
                      <Link 
                        to="/coordinator/events"
                        className="text-xs bg-primary text-white px-3 py-1 rounded-full"
                      >
                        Participant List
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <FontAwesomeIcon icon={faCalendarDays} className="text-4xl text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-2">No events to manage</p>
                  <p className="text-gray-400 text-sm mb-4">
                    As a coordinator, you can create and manage mountain biking events for the community
                  </p>
                  <Link to="/coordinator/events" className="text-primary text-sm mt-2 inline-block">
                    Create your first event
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Admin View */}
        <section className={activeRole !== 'admin' ? 'hidden' : 'mb-3'}>
          <div className="bg-white mb-3">
            <div className="px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-800">System Statistics</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                <div className="text-orange-500 mb-1">
                  <FontAwesomeIcon icon={faUserGroup} className="text-xl" />
                </div>
                <p className="text-xs text-gray-600">Total Users</p>
                <h4 className="text-xl font-bold">{adminStats?.totalUsers || 0}</h4>
              </div>
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <div className="text-green-500 mb-1">
                  <FontAwesomeIcon icon={faCalendarDays} className="text-xl" />
                </div>
                <p className="text-xs text-gray-600">Total Events</p>
                <h4 className="text-xl font-bold">{adminStats?.totalEvents || 0}</h4>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <div className="text-purple-500 mb-1">
                  <FontAwesomeIcon icon={faChild} className="text-xl" />
                </div>
                <p className="text-xs text-gray-600">Registered Minors</p>
                <h4 className="text-xl font-bold">{adminStats?.registeredMinors || 0}</h4>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <div className="text-amber-500 mb-1">
                  <FontAwesomeIcon icon={faUserTie} className="text-xl" />
                </div>
                <p className="text-xs text-gray-600">Coordinators</p>
                <h4 className="text-xl font-bold">{adminStats?.coordinators || 0}</h4>
              </div>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white mb-3">
            <div className="px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-800">This Month</h3>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{adminStats?.systemHealth.eventsThisMonth || 0}</p>
                <p className="text-xs text-gray-600">New Events</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{adminStats?.systemHealth.registrationsThisMonth || 0}</p>
                <p className="text-xs text-gray-600">Registrations</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{adminStats?.systemHealth.activeUsers || 0}</p>
                <p className="text-xs text-gray-600">Active Users</p>
              </div>
            </div>
          </div>

          <div className="bg-white">
            <div className="px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-800">Quick Actions</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <Link to="/admin/users" className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col items-center justify-center hover:bg-gray-50">
                <FontAwesomeIcon icon={faUsers} className="text-primary text-xl mb-2" />
                <span className="text-sm">Manage Users</span>
              </Link>
              <Link to="/admin/events" className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col items-center justify-center hover:bg-gray-50">
                <FontAwesomeIcon icon={faCalendarDays} className="text-primary text-xl mb-2" />
                <span className="text-sm">Manage Events</span>
              </Link>
              <Link to="/admin/notifications" className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col items-center justify-center hover:bg-gray-50">
                <FontAwesomeIcon icon={faBell} className="text-primary text-xl mb-2" />
                <span className="text-sm">Manage Notifications</span>
              </Link>
              <Link to="/admin/coordinators" className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col items-center justify-center hover:bg-gray-50">
                <FontAwesomeIcon icon={faUserTie} className="text-primary text-xl mb-2" />
                <span className="text-sm">Manage Coordinators</span>
              </Link>
            </div>
          </div>
        </section>


      </main>

      {/* Bottom Navigation */}
      <nav className="app-bottom-nav">
        <div className="flex justify-around">
          <Link to="/dashboard" className="app-bottom-nav-link-active py-2 px-3">
            <FontAwesomeIcon icon={faHome} className="text-lg" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/events" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faCalendarDays} className="text-lg" />
            <span className="text-xs mt-1">Events</span>
          </Link>
          <Link to="/profile" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faUser} className="text-lg" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
          <Link to="/dashboard/alerts" className="app-bottom-nav-link py-2 px-3 relative">
            <FontAwesomeIcon icon={faBell} className="text-lg" />
            <span className="text-xs mt-1">Alerts</span>
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </Link>
        </div>
      </nav>

      {/* Coordinator Upgrade Modal */}
              {currentUser?.email && (
        <CoordinatorUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            // Reload coordinator data after modal closes
            if (currentUser.email) {
              loadCoordinatorUpgradeData(currentUser.email);
            }
          }}
          userEmail={currentUser.email}
          userName={currentUser.name || 'User'}
        />
      )}
    </div>
  );
};

export default Dashboard;
